import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage, auth } from "./firebase";
import { logAuditEvent, AuditEvents } from "./auditService";
import { updateSession } from "./dataService";
import { orgStoragePath } from "./orgPaths";

/**
 * AudioRecorderService
 * 
 * Handles audio recording, local storage, trigger phrase detection,
 * and upload to Firebase Storage.
 * 
 * TRIGGER SYSTEM:
 * - Fuzzy matching: partial phrase matches and natural variations
 * - Priority levels: "normal" flags vs "priority" flags
 * - Auto-close: flags automatically close after a set duration 
 *   if no explicit end trigger is spoken
 * - Section routing: routes content to specific note sections
 */

// Auto-close flag after this many seconds if no end trigger
const AUTO_FLAG_DURATION = 30;

// ===== THREE-TIER TRIGGER SYSTEM =====
//
// TIER 1 — LOCAL FLAGS (yellow): Agent observations. NOT pushed to supervisor.
//   "I'm concerned", "that concerns me", "red flag", "need to follow up"
//   These are things the AGENT says during normal clinical work.
//   Captured as flagged notes → show in generated note. No real-time alert.
//
// TIER 2 — CLINICAL INDICATORS (orange): Individual disclosures that are clinically relevant.
//   "depressed", "not sleeping", "stopped going to groups", "hearing voices"
//   Captured as elevated flags → highlighted in note. Supervisor sees during review.
//   No real-time dashboard alert.
//
// TIER 3 — REAL-TIME ALERTS (red): Actual crises and exploitation.
//   "want to kill myself", "he's been hitting me", "sent money to someone online"
//   Pushed to Firestore IMMEDIATELY. Supervisor dashboard beeps.

const DEFAULT_TRIGGERS = {
  // === FLAG START TRIGGERS ===
  startNote: {
    priority: "normal",
    phrases: [
      "take this note", "take note", "take a note",
      "flag this", "flag that",
      "make a note", "make note of this", "make note of that",
      "note this", "note that",
      "i should note", "i should note this",
      "let me note", "let me note this",
      "document this", "document that",
      "record this", "record that",
      "that's important", "this is important",
      "i want to remember", "i want to remember this",
      "don't forget", "don't let me forget",
      "write this down", "write that down",
      "mark this", "mark that",
      "capture this", "capture that",
      "new note",
    ],
  },

  // === FLAG END TRIGGERS ===
  endNote: {
    priority: null,
    phrases: [
      "end note", "note complete", "stop note",
      "got it", "that's it", "that's all",
      "okay moving on", "moving on",
      "done with that", "done with note",
      "close note",
    ],
  },

  // === TIER 1: LOCAL FLAGS (agent observations — NO supervisor alert) ===
  localFlag: {
    priority: "local",
    tier: 1,
    phrases: [
      "i'm concerned", "i am concerned",
      "this concerns me", "that concerns me",
      "i'm worried", "i am worried", "this worries me",
      "red flag", "this is a red flag",
      "safety concern", "safety issue",
      "i need to follow up", "need to follow up",
      "this needs attention", "needs attention",
      "let me call my supervisor", "call my supervisor",
      "i need to talk to my supervisor",
      "bring in additional help", "need additional help",
      "need backup", "need some backup",
      "escalate this", "needs to be escalated",
      "i need to report this",
      "defcon", "defcon 4", "defcon 3",
    ],
  },

  // === TIER 2: CLINICAL INDICATORS (individual disclosures — flagged in note) ===
  clinicalIndicator: {
    priority: "clinical",
    tier: 2,
    categories: {
      mood: {
        label: "Mood Concern",
        phrases: [
          "depressed", "feeling depressed", "a little depressed",
          "feeling down", "feeling low", "feeling sad",
          "hopeless", "feel hopeless", "no hope",
          "can't sleep", "not sleeping", "insomnia",
          "not eating", "no appetite", "lost my appetite",
          "no energy", "feel tired all the time",
          "don't want to do anything", "don't care anymore",
          "crying a lot", "been crying", "can't stop crying",
          "feeling empty", "feel numb", "feel nothing",
          "anxious", "anxiety", "panic attack", "panicking",
          "stressed out", "overwhelmed", "can't handle it",
          "irritable", "angry all the time", "snapping at people",
        ],
      },
      symptoms: {
        label: "Symptom Report",
        phrases: [
          "hearing voices", "the voices", "voices told me",
          "seeing things", "hallucinating",
          "paranoid", "people are following me", "being watched",
          "can't concentrate", "can't focus", "mind is racing",
          "not thinking clearly", "confused",
          "flashback", "having flashbacks", "nightmares",
          "dissociating", "zoning out", "losing time",
        ],
      },
      medication: {
        label: "Medication Concern",
        phrases: [
          "side effects", "making me feel", "makes me feel like a zombie",
          "don't like how it makes me feel",
          "ran out of medication", "ran out of meds",
          "missed a dose", "forgot to take",
          "pharmacy won't fill", "can't afford my meds",
          "doctor changed my medication",
          "taking less", "cutting my dose",
        ],
      },
      engagement: {
        label: "Engagement Concern",
        phrases: [
          "stopped going to groups", "don't want to go",
          "missed my appointment", "didn't go to my appointment",
          "don't want services", "don't need help",
          "thinking about leaving the program",
          "don't want to talk about it",
          "haven't left the house", "staying in bed",
        ],
      },
      substance: {
        label: "Substance Concern",
        phrases: [
          "had a drink last night", "had a couple beers", "been drinking lately",
          "smoked some weed", "smoked a joint",
          "thought about using", "been having cravings", "having cravings",
          "someone offered me drugs", "my friend was using around me",
          "went to the bar last night", "was at a party with alcohol",
          "been on a bender", "had a slip", "had a setback",
        ],
      },
    },
  },

  // === TIER 3: REAL-TIME ALERTS (actual crises — push to supervisor NOW) ===
  realtimeAlert: {
    priority: "priority",
    tier: 3,
    categories: {
      suicidal: {
        label: "Suicidal Ideation",
        severity: "critical",
        phrases: [
          "want to kill myself", "wanna kill myself",
          "about to kill myself", "gonna kill myself", "going to kill myself",
          "i'm going to kill myself", "i'm about to kill myself",
          "want to die", "wanna die", "wish i was dead",
          "better off dead", "everyone would be better off",
          "don't want to be here anymore", "don't want to live",
          "going to end it", "end it all", "about to end it",
          "thinking about suicide", "i'm suicidal",
          "have a plan to hurt myself", "plan to kill myself",
          "wrote a goodbye", "writing a goodbye",
          "gave away my things", "saying goodbye to everyone",
          "tried to kill myself", "attempted suicide",
          "cutting myself", "i've been cutting",
          "hurting myself", "harming myself",
          "took a bunch of pills", "i overdosed on pills",
          "might kill myself", "might as well kill myself",
          "ready to die", "i just want it to be over",
        ],
      },
      homicidal: {
        label: "Homicidal Ideation",
        severity: "critical",
        phrases: [
          "want to kill him", "want to kill her", "want to kill them",
          "wanna kill him", "wanna kill her",
          "going to kill him", "going to kill her", "going to kill someone",
          "gonna kill him", "gonna kill her", "gonna kill someone",
          "about to kill him", "about to kill her", "about to kill someone",
          "i'm going to kill", "i'm about to kill", "i'm gonna kill",
          "going to hurt someone", "going to hurt him", "going to hurt her",
          "i'm homicidal", "having homicidal thoughts",
          "going to get my gun", "going to get a weapon",
          "i'll make them pay", "i'll make him pay", "i'll make her pay",
          "kill you", "i'll kill you", "imma kill you",
          "kill somebody", "imma kill somebody", "gonna kill somebody",
        ],
      },
      abuse: {
        label: "Abuse / Neglect",
        severity: "critical",
        phrases: [
          "hitting me", "he hits me", "she hits me",
          "beat me", "beats me up", "put hands on me",
          "molested me", "sexually assaulted me", "raped me",
          "my kids aren't safe", "hurting my children",
          "locked me in", "won't let me leave", "holding me against my will",
          "not feeding me", "no food in the house",
          "living in filth", "no electricity", "no running water",
          "need to file mandatory report", "mandatory report needed",
          "bruises all over", "marks all over", "covered in bruises",
        ],
      },
      exploitation: {
        label: "Financial Exploitation",
        severity: "high",
        phrases: [
          "sent her money", "sent him money", "sent them money",
          "gave her money", "gave him money", "gave them money",
          "they asked me for money", "he asked me for money", "she asked me for money",
          "took my check", "stole my check", "cashed my check",
          "stole from me", "stole my money", "stealing from me",
          "gave them my card", "gave her my card", "gave him my card number",
          "drained my account", "drained my bank account",
          "someone i met online", "met someone online asking for money",
          "wired money to someone", "sent a wire transfer",
          "buying gift cards for someone", "they want me to buy gift cards",
          "won't pay me back", "never paid me back",
          "taking my disability check", "taking my SSI check",
          "controlling my money", "won't let me have my money",
        ],
      },
      substanceCrisis: {
        label: "Substance Crisis",
        severity: "high",
        phrases: [
          "relapsed", "i relapsed", "started using again",
          "overdosed", "i overdosed",
          "back on drugs", "back on meth", "back on heroin", "back on crack",
          "shooting up", "using needles", "using IV",
          "can't stop drinking", "can't stop using",
          "drinking every day", "using every day",
          "haven't been sober",
        ],
      },
      medNonCompliance: {
        label: "Medication Non-Compliance",
        severity: "high",
        phrases: [
          "stopped taking my medication", "stopped taking my meds",
          "not taking my medication", "not taking my meds",
          "haven't taken my meds", "haven't taken medication",
          "threw away my medication", "flushed my meds",
          "not going to take it", "refuse to take",
          "stopped my meds weeks ago", "been off my meds",
        ],
      },
      housingCrisis: {
        label: "Housing Crisis",
        severity: "high",
        phrases: [
          "getting evicted", "eviction notice",
          "homeless", "nowhere to go", "on the street",
          "sleeping in my car", "sleeping outside",
          "kicked out", "kicked me out",
          "house is condemned", "no utilities",
          "shelter is full", "can't find shelter",
          "living under a bridge", "living in a tent",
        ],
      },
    },
  },

  // === SECTION ROUTING TRIGGERS (unchanged) ===
  medication: {
    priority: null,
    phrases: [
      "medication check", "med check", "medication review",
      "let's talk about medications", "talk about your meds",
      "are you taking your medication", "taking your meds",
      "medication compliance", "med compliance",
      "refills", "prescription",
    ],
  },

  safety: {
    priority: null,
    phrases: [
      "safety assessment", "safety check", "risk assessment",
      "safety plan", "review safety plan",
      "let's review your safety plan",
    ],
  },

  housing: {
    priority: null,
    phrases: [
      "housing check", "housing situation",
      "where are you living", "living situation",
      "your apartment", "your house", "your home",
      "eviction", "lease", "rent",
    ],
  },

  coping: {
    priority: null,
    phrases: [
      "coping skills", "coping check",
      "how are you coping", "how are you handling",
      "stress management", "what do you do when",
      "recovery activities", "support groups",
      "NA meeting", "AA meeting",
    ],
  },

  // === SESSION END TRIGGERS ===
  endSession: {
    priority: null,
    phrases: [
      "end session", "stop session", "session complete",
      "wrap up", "let's wrap up", "that's our time",
      "we're done", "session over",
    ],
  },
};

class AudioRecorderService {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.recognition = null;
    this.isRecording = false;
    this.isPaused = false;
    this.isFlagging = false;
    this.flagPriority = "normal";
    this.startTime = null;
    this.sessionId = null;
    this.triggers = JSON.parse(JSON.stringify(DEFAULT_TRIGGERS));
    this.flaggedSegments = [];
    this.transcriptBuffer = [];
    this.currentSection = null;
    this.currentFlagStart = null;
    this.autoFlagTimer = null;

    // Callbacks
    this.onTranscript = null;
    this.onTriggerDetected = null;
    this.onFlaggedNote = null;
    this.onError = null;
    this.onSectionChange = null;
    this.onSessionEnd = null;
    this.onFlagStart = null; // NEW: called when a flag starts

    this.setupOfflineRetry();
  }

  /**
   * Listen for online events and retry pending uploads. Also try once on
   * startup in case we're online but have pending recordings from a prior
   * session that crashed before upload.
   */
  setupOfflineRetry() {
    if (typeof window === "undefined") return;
    window.addEventListener("online", () => {
      this.retryPendingUploads();
    });
    // Delay a few seconds on startup so auth has time to settle.
    setTimeout(() => {
      if (navigator.onLine) this.retryPendingUploads();
    }, 8000);
  }

  /**
   * Initialize audio recording and speech recognition
   */
  async initialize(sessionId, customTriggers = null) {
    this.sessionId = sessionId;
    if (customTriggers) {
      // Merge custom triggers with defaults
      for (const [key, value] of Object.entries(customTriggers)) {
        if (this.triggers[key]) {
          this.triggers[key].phrases = [
            ...this.triggers[key].phrases,
            ...value,
          ];
        }
      }
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
        },
      });

      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
          this.saveChunkLocally(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.handleRecordingStop();
      };

      this.initializeSpeechRecognition();

      return { success: true };
    } catch (error) {
      console.error("Audio initialization error:", error);
      if (this.onError) this.onError(error);
      return {
        success: false,
        error: error.name === "NotAllowedError"
          ? "Microphone access denied. Please enable microphone permissions."
          : "Failed to initialize audio. Please check your device settings.",
      };
    }
  }

  getSupportedMimeType() {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return "audio/webm";
  }

  initializeSpeechRecognition() {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn("Speech Recognition not available — trigger phrases disabled");
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = "en-US";
    this.recognition.maxAlternatives = 1;

    this.recognition.onresult = (event) => {
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (this.onTranscript) {
        this.onTranscript({
          final: finalTranscript,
          interim: interimTranscript,
          timestamp: this.getElapsedTime(),
        });
      }

      if (finalTranscript) {
        this.transcriptBuffer.push({
          text: finalTranscript,
          timestamp: this.getElapsedTime(),
          section: this.currentSection,
          flagged: this.isFlagging,
        });
        this.checkForTriggers(finalTranscript.toLowerCase().trim());
      }
    };

    this.recognition.onerror = (event) => {
      if (event.error !== "no-speech") {
        console.warn("Speech recognition error:", event.error);
      }
      if (this.isRecording && !this.isPaused && event.error !== "not-allowed") {
        setTimeout(() => {
          try { this.recognition.start(); } catch (e) {}
        }, 500);
      }
    };

    this.recognition.onend = () => {
      if (this.isRecording && !this.isPaused) {
        try { this.recognition.start(); } catch (e) {}
      }
    };
  }

  start() {
    if (this.isRecording) return;

    this.isRecording = true;
    this.isPaused = false;
    this.startTime = Date.now();
    this.audioChunks = [];
    this.flaggedSegments = [];
    this.transcriptBuffer = [];

    this.mediaRecorder.start(5000);

    if (this.recognition) {
      try { this.recognition.start(); } catch (e) {}
    }

    logAuditEvent(AuditEvents.RECORDING_START, {
      sessionId: this.sessionId,
    });
  }

  pause() {
    if (!this.isRecording || this.isPaused) return;
    this.isPaused = true;
    this.mediaRecorder.pause();
    if (this.recognition) {
      try { this.recognition.stop(); } catch (e) {}
    }
  }

  resume() {
    if (!this.isRecording || !this.isPaused) return;
    this.isPaused = false;
    this.mediaRecorder.resume();
    if (this.recognition) {
      try { this.recognition.start(); } catch (e) {}
    }
  }

  stop() {
    if (!this.isRecording) return;

    // Auto-close any open flag
    if (this.isFlagging) {
      this.closeCurrentFlag();
    }

    this.isRecording = false;
    this.isPaused = false;

    this.mediaRecorder.stop();
    if (this.recognition) {
      try { this.recognition.stop(); } catch (e) {}
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    if (this.autoFlagTimer) {
      clearTimeout(this.autoFlagTimer);
      this.autoFlagTimer = null;
    }

    logAuditEvent(AuditEvents.RECORDING_STOP, {
      sessionId: this.sessionId,
      duration: this.getElapsedTime(),
      flaggedSegments: this.flaggedSegments.length,
    });
  }

  /**
   * Fuzzy match: check if text contains any phrase from a list
   * Uses includes() but also checks for partial matches
   */
  fuzzyMatch(text, phrases) {
    const normalizedText = text.toLowerCase().replace(/['']/g, "'").trim();
    for (const phrase of phrases) {
      const normalizedPhrase = phrase.toLowerCase();
      // Simple includes check — phrase must appear in the text
      if (normalizedText.includes(normalizedPhrase)) {
        return phrase;
      }
    }
    return null;
  }

  /**
   * Check transcript for trigger phrases with fuzzy matching.
   * Three-tier system:
   *   Tier 1 (local): Agent observations → flag in note only
   *   Tier 2 (clinical): Individual disclosures → flag + highlight in note
   *   Tier 3 (realtime): Crisis/exploitation → push alert to supervisor NOW
   */
  checkForTriggers(text) {
    // 1. Check TIER 3 — REAL-TIME ALERTS (highest priority)
    const realtimeCategories = this.triggers.realtimeAlert?.categories || {};
    for (const [catKey, cat] of Object.entries(realtimeCategories)) {
      const match = this.fuzzyMatch(text, cat.phrases);
      if (match) {
        if (this.isFlagging) this.closeCurrentFlag();
        
        this.startFlag("priority", match);
        this.startAutoCloseTimer();

        if (this.onTriggerDetected) {
          this.onTriggerDetected({
            type: "realtimeAlert",
            tier: 3,
            priority: "priority",
            phrase: match,
            category: cat.label,
            categoryKey: catKey,
            severity: cat.severity,
            timestamp: this.getElapsedTime(),
            transcriptContext: this.getRecentTranscript(Math.max(0, this.getElapsedTime() - 30)),
          });
        }
        logAuditEvent(AuditEvents.RECORDING_TRIGGER, {
          type: "realtimeAlert",
          tier: 3,
          category: cat.label,
          phrase: match,
          sessionId: this.sessionId,
        });
        return;
      }
    }

    // 2. Check TIER 2 — CLINICAL INDICATORS
    const clinicalCategories = this.triggers.clinicalIndicator?.categories || {};
    for (const [catKey, cat] of Object.entries(clinicalCategories)) {
      const match = this.fuzzyMatch(text, cat.phrases);
      if (match) {
        if (this.isFlagging) this.closeCurrentFlag();

        this.startFlag("clinical", match);
        this.startAutoCloseTimer();

        if (this.onTriggerDetected) {
          this.onTriggerDetected({
            type: "clinicalIndicator",
            tier: 2,
            priority: "clinical",
            phrase: match,
            category: cat.label,
            categoryKey: catKey,
            timestamp: this.getElapsedTime(),
            transcriptContext: this.getRecentTranscript(Math.max(0, this.getElapsedTime() - 20)),
          });
        }
        return;
      }
    }

    // 3. Check TIER 1 — LOCAL FLAGS (agent observations)
    const localMatch = this.fuzzyMatch(text, (this.triggers.localFlag?.phrases || []));
    if (localMatch) {
      if (this.isFlagging) this.closeCurrentFlag();

      this.startFlag("local", localMatch);
      this.startAutoCloseTimer();

      if (this.onTriggerDetected) {
        this.onTriggerDetected({
          type: "localFlag",
          tier: 1,
          priority: "local",
          phrase: localMatch,
          category: "Agent Observation",
          timestamp: this.getElapsedTime(),
        });
      }
      return;
    }

    // 4. Check end note triggers
    if (this.isFlagging) {
      const endMatch = this.fuzzyMatch(text, this.triggers.endNote.phrases);
      if (endMatch) {
        this.closeCurrentFlag();
        if (this.onTriggerDetected) {
          this.onTriggerDetected({
            type: "endNote",
            phrase: endMatch,
            timestamp: this.getElapsedTime(),
          });
        }
        return;
      }
    }

    // 5. Check start note triggers
    const startMatch = this.fuzzyMatch(text, this.triggers.startNote.phrases);
    if (startMatch && !this.isFlagging) {
      this.startFlag("normal", startMatch);
      this.startAutoCloseTimer();

      if (this.onTriggerDetected) {
        this.onTriggerDetected({
          type: "startNote",
          priority: "normal",
          phrase: startMatch,
          timestamp: this.getElapsedTime(),
        });
      }
      logAuditEvent(AuditEvents.RECORDING_TRIGGER, {
        type: "startNote",
        phrase: startMatch,
        sessionId: this.sessionId,
      });
      return;
    }

    // 6. Check section routing triggers
    const sectionTriggers = [
      { key: "medication", section: "medication" },
      { key: "safety", section: "safety" },
      { key: "housing", section: "housing" },
      { key: "coping", section: "coping" },
    ];

    for (const { key, section } of sectionTriggers) {
      const match = this.fuzzyMatch(text, this.triggers[key].phrases);
      if (match) {
        this.currentSection = section;
        if (this.onSectionChange) this.onSectionChange(section);
        if (this.onTriggerDetected) {
          this.onTriggerDetected({
            type: key,
            phrase: match,
            timestamp: this.getElapsedTime(),
          });
        }
        return;
      }
    }

    // 7. Check end session trigger
    const endMatch = this.fuzzyMatch(text, this.triggers.endSession.phrases);
    if (endMatch) {
      if (this.onSessionEnd) this.onSessionEnd();
      if (this.onTriggerDetected) {
        this.onTriggerDetected({
          type: "endSession",
          phrase: endMatch,
          timestamp: this.getElapsedTime(),
        });
      }
      return;
    }
  }

  /**
   * Start a new flag capture
   */
  startFlag(priority, phrase) {
    this.isFlagging = true;
    this.flagPriority = priority;
    this.currentFlagStart = this.getElapsedTime();

    if (this.onFlagStart) {
      this.onFlagStart({
        priority,
        phrase,
        timestamp: this.getElapsedTime(),
      });
    }
  }

  /**
   * Close the current flag and save the segment
   */
  closeCurrentFlag() {
    if (!this.isFlagging) return;

    if (this.autoFlagTimer) {
      clearTimeout(this.autoFlagTimer);
      this.autoFlagTimer = null;
    }

    const segment = {
      startTime: this.currentFlagStart,
      endTime: this.getElapsedTime(),
      transcript: this.getRecentTranscript(this.currentFlagStart),
      priority: this.flagPriority,
    };

    this.flaggedSegments.push(segment);
    this.isFlagging = false;
    this.flagPriority = "normal";

    if (this.onFlaggedNote) this.onFlaggedNote(segment);
  }

  /**
   * Auto-close flag after set duration
   */
  startAutoCloseTimer() {
    if (this.autoFlagTimer) {
      clearTimeout(this.autoFlagTimer);
    }
    this.autoFlagTimer = setTimeout(() => {
      if (this.isFlagging) {
        this.closeCurrentFlag();
        if (this.onTriggerDetected) {
          this.onTriggerDetected({
            type: "autoClose",
            phrase: "Auto-closed after " + AUTO_FLAG_DURATION + "s",
            timestamp: this.getElapsedTime(),
          });
        }
      }
    }, AUTO_FLAG_DURATION * 1000);
  }

  getRecentTranscript(fromTime) {
    return this.transcriptBuffer
      .filter((entry) => entry.timestamp >= fromTime)
      .map((entry) => entry.text)
      .join(" ");
  }

  getElapsedTime() {
    if (!this.startTime) return 0;
    return Math.floor((Date.now() - this.startTime) / 1000);
  }

  getFormattedTime() {
    const elapsed = this.getElapsedTime();
    const mins = Math.floor(elapsed / 60).toString().padStart(2, "0");
    const secs = (elapsed % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  }

  async handleRecordingStop() {
    const audioBlob = new Blob(this.audioChunks, {
      type: this.getSupportedMimeType(),
    });
    await this.saveCompleteRecording(audioBlob);

    // Fire-and-forget upload to Firebase Storage. This is the defense
    // against device storage pressure evicting the local IndexedDB copy.
    // Offline: uploadRecording returns { success: false, offline: true } and
    // we simply skip the session update. The local copy stays in IndexedDB
    // and the Recovery page can still surface it.
    this.uploadAndLinkRecording(audioBlob).catch((e) => {
      console.error("[Audio] uploadAndLink failed:", e);
    });

    return {
      audioBlob,
      duration: this.getElapsedTime(),
      transcript: this.transcriptBuffer,
      flaggedSegments: this.flaggedSegments,
    };
  }

  /**
   * Upload the assembled audio blob to Firebase Storage and stamp the
   * session document with the resulting download URL. Non-blocking.
   */
  async uploadAndLinkRecording(audioBlob) {
    const sessionId = this.sessionId;
    if (!sessionId) return;

    const result = await this.uploadRecordingByPath(audioBlob, sessionId);

    if (!result.success) {
      if (result.offline) {
        console.log("[Audio] Upload deferred — offline. Will retry when online.");
      } else {
        console.warn("[Audio] Upload failed:", result.error);
      }
      return;
    }

    // Mark the local IndexedDB row as uploaded so retryPendingUploads skips it.
    await this.markRecordingUploaded(sessionId, result.url);

    // Stamp the Firestore session doc so admin / billing can find the audio.
    if (!sessionId.startsWith("local-")) {
      try {
        await updateSession(sessionId, {
          audioUploaded: true,
          audioUrl: result.url,
          audioUploadedAt: new Date().toISOString(),
          audioSize: audioBlob.size,
        });
      } catch (e) {
        console.warn("[Audio] Session doc update with audioUrl failed:", e);
      }
    }
  }

  async saveChunkLocally(chunk) {
    try {
      const dbRequest = indexedDB.open("FieldNotesAudio", 1);
      dbRequest.onupgradeneeded = (event) => {
        const idb = event.target.result;
        if (!idb.objectStoreNames.contains("chunks")) {
          idb.createObjectStore("chunks", { autoIncrement: true });
        }
        if (!idb.objectStoreNames.contains("recordings")) {
          idb.createObjectStore("recordings", { keyPath: "sessionId" });
        }
      };
      dbRequest.onsuccess = (event) => {
        const idb = event.target.result;
        const tx = idb.transaction("chunks", "readwrite");
        tx.objectStore("chunks").add({
          sessionId: this.sessionId,
          data: chunk,
          timestamp: Date.now(),
        });
      };
    } catch (e) {
      console.error("Failed to save audio chunk locally:", e);
    }
  }

  async saveCompleteRecording(audioBlob) {
    return new Promise((resolve, reject) => {
      const dbRequest = indexedDB.open("FieldNotesAudio", 1);
      dbRequest.onupgradeneeded = (event) => {
        const idb = event.target.result;
        if (!idb.objectStoreNames.contains("recordings")) {
          idb.createObjectStore("recordings", { keyPath: "sessionId" });
        }
      };
      dbRequest.onsuccess = (event) => {
        const idb = event.target.result;
        const tx = idb.transaction("recordings", "readwrite");
        tx.objectStore("recordings").put({
          sessionId: this.sessionId,
          audio: audioBlob,
          transcript: this.transcriptBuffer,
          flaggedSegments: this.flaggedSegments,
          duration: this.getElapsedTime(),
          timestamp: Date.now(),
          uploaded: false,
        });
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      };
      dbRequest.onerror = () => reject(dbRequest.error);
    });
  }

  /**
   * Mark a recording in IndexedDB as uploaded so it doesn't get retried.
   */
  async markRecordingUploaded(sessionId, audioUrl) {
    try {
      const idb = await new Promise((resolve, reject) => {
        const req = indexedDB.open("FieldNotesAudio", 1);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      const tx = idb.transaction("recordings", "readwrite");
      const store = tx.objectStore("recordings");
      const getReq = store.get(sessionId);
      await new Promise((resolve) => {
        getReq.onsuccess = () => {
          const row = getReq.result;
          if (row) {
            store.put({ ...row, uploaded: true, audioUrl });
          }
          resolve();
        };
        getReq.onerror = () => resolve();
      });
    } catch (e) {
      console.warn("[Audio] markRecordingUploaded failed:", e);
    }
  }

  /**
   * Upload any recordings in IndexedDB that haven't been uploaded yet.
   * Called on window 'online' event and once shortly after startup.
   * Uses an explicit sessionId per row so the active session's this.sessionId
   * is not touched.
   */
  async retryPendingUploads() {
    if (this._retryInFlight) return;
    this._retryInFlight = true;
    try {
      const idb = await new Promise((resolve, reject) => {
        const req = indexedDB.open("FieldNotesAudio", 1);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      const rows = await new Promise((resolve) => {
        try {
          const tx = idb.transaction("recordings", "readonly");
          const req = tx.objectStore("recordings").getAll();
          req.onsuccess = () => resolve(req.result || []);
          req.onerror = () => resolve([]);
        } catch (e) { resolve([]); }
      });

      const pending = rows.filter(
        (r) => !r.uploaded && r.audio && r.sessionId
      );
      if (pending.length === 0) return;

      console.log(`[Audio] Retrying ${pending.length} pending upload(s)`);

      for (const row of pending) {
        try {
          const result = await this.uploadRecordingByPath(row.audio, row.sessionId);
          if (result.success) {
            await this.markRecordingUploaded(row.sessionId, result.url);
            if (row.sessionId && !row.sessionId.startsWith("local-")) {
              try {
                await updateSession(row.sessionId, {
                  audioUploaded: true,
                  audioUrl: result.url,
                  audioUploadedAt: new Date().toISOString(),
                  audioSize: row.audio.size,
                });
              } catch (e) {
                console.warn("[Audio] Session doc update on retry failed:", e);
              }
            }
          } else if (!result.offline) {
            console.warn(`[Audio] Retry upload failed for ${row.sessionId}:`, result.error);
          }
        } catch (e) {
          console.warn(`[Audio] Retry crashed for ${row.sessionId}:`, e);
        }
      }
    } catch (e) {
      console.warn("[Audio] retryPendingUploads scan failed:", e);
    } finally {
      this._retryInFlight = false;
    }
  }

  /**
   * Upload a blob to a specific sessionId path. Used both for the active
   * upload path (uploadAndLinkRecording) and for the offline retry path.
   */
  async uploadRecordingByPath(audioBlob, sessionId) {
    if (!navigator.onLine) {
      return { success: false, offline: true };
    }
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error("Not authenticated");

      const audioRef = ref(
        storage,
        orgStoragePath("audio", userId, sessionId, "recording.webm")
      );

      await uploadBytes(audioRef, audioBlob, {
        contentType: this.getSupportedMimeType(),
        customMetadata: {
          sessionId: sessionId,
          size: String(audioBlob.size),
        },
      });

      const downloadURL = await getDownloadURL(audioRef);

      logAuditEvent(AuditEvents.RECORDING_UPLOAD, {
        sessionId: sessionId,
        size: audioBlob.size,
      });

      return { success: true, url: downloadURL };
    } catch (error) {
      console.error("[Audio] Upload failed:", error);
      return { success: false, error: error.message };
    }
  }

  async uploadRecording(audioBlob) {
    if (!navigator.onLine) {
      return { success: false, offline: true };
    }

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error("Not authenticated");

      const audioRef = ref(
        storage,
        orgStoragePath("audio", userId, this.sessionId, "recording.webm")
      );

      await uploadBytes(audioRef, audioBlob, {
        contentType: this.getSupportedMimeType(),
        customMetadata: {
          sessionId: this.sessionId,
          duration: String(this.getElapsedTime()),
          flaggedSegments: String(this.flaggedSegments.length),
        },
      });

      const downloadURL = await getDownloadURL(audioRef);

      logAuditEvent(AuditEvents.RECORDING_UPLOAD, {
        sessionId: this.sessionId,
        size: audioBlob.size,
      });

      return { success: true, url: downloadURL };
    } catch (error) {
      console.error("Upload failed:", error);
      return { success: false, error: error.message };
    }
  }

  getSessionData() {
    return {
      sessionId: this.sessionId,
      duration: this.getElapsedTime(),
      transcript: this.transcriptBuffer,
      flaggedSegments: this.flaggedSegments,
      sections: this.groupTranscriptBySections(),
    };
  }

  groupTranscriptBySections() {
    const sections = {};
    for (const entry of this.transcriptBuffer) {
      const section = entry.section || "general";
      if (!sections[section]) sections[section] = [];
      sections[section].push(entry);
    }
    return sections;
  }

  destroy() {
    this.stop();
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
    if (this.autoFlagTimer) {
      clearTimeout(this.autoFlagTimer);
    }
    this.mediaRecorder = null;
    this.recognition = null;
    this.stream = null;
  }
}

export default new AudioRecorderService();